const BASE_URL = 'https://bigbirdtravel.com';

// ── TravelAgency + LocalBusiness combined ─────────────────────────────────────
export const AGENCY_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': ['TravelAgency', 'LocalBusiness'],
  name: 'Big Bird Travel and Tourism',
  alternateName: 'Big Bird Travel UAE',
  url: BASE_URL,
  logo: `${BASE_URL}/logo.png`,
  image: `${BASE_URL}/og-cover.jpg`,
  telephone: '+971561789642',
  email: 'info@bigbirdtravel.com',
  address: {
    '@type': 'PostalAddress',
    streetAddress: 'Al Sawari Street 5, Building No-21, Office No-4, M-13',
    addressLocality: 'Abu Dhabi',
    addressRegion: 'Abu Dhabi',
    addressCountry: 'AE',
  },
  geo: {
    '@type': 'GeoCoordinates',
    latitude: 24.3516,
    longitude: 54.4966,
  },
  hasMap: 'https://maps.google.com/?q=Al+Sawari+Street+5+Building+21+Mussafah+M-13+Abu+Dhabi+UAE',
  priceRange: 'AED 1,399 – AED 25,000',
  currenciesAccepted: 'AED',
  paymentAccepted: 'Cash, Credit Card, Bank Transfer',
  openingHoursSpecification: [
    {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
      opens: '09:00',
      closes: '18:00',
    },
    {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Saturday'],
      opens: '10:00',
      closes: '16:00',
    },
  ],
  aggregateRating: {
    '@type': 'AggregateRating',
    ratingValue: '4.7',
    reviewCount: '29',
    bestRating: '5',
    worstRating: '1',
  },
  sameAs: [
    'https://www.instagram.com/bigbirdtraveluae/',
    'https://www.facebook.com/profile.php?id=61558812744882',
  ],
  areaServed: [
    { '@type': 'City', name: 'Dubai' },
    { '@type': 'City', name: 'Abu Dhabi' },
    { '@type': 'AdministrativeArea', name: 'United Arab Emirates' },
  ],
};

// ── FAQ schema ────────────────────────────────────────────────────────────────
export function faqSchema(faqs: { q: string; a: string }[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(f => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };
}

// ── BreadcrumbList schema ─────────────────────────────────────────────────────
export function breadcrumbSchema(items: { name: string; item: string }[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((it, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: it.name,
      item: `${BASE_URL}${it.item}`,
    })),
  };
}

// ── Service schema ────────────────────────────────────────────────────────────
export function serviceSchema(name: string, description: string, url: string) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name,
    description,
    provider: {
      '@type': 'TravelAgency',
      name: 'Big Bird Travel and Tourism',
      url: BASE_URL,
    },
    url: `${BASE_URL}${url}`,
    areaServed: {
      '@type': 'Country',
      name: 'United Arab Emirates',
    },
  };
}

// ── TourPackage / Product schema ──────────────────────────────────────────────
export function packageSchema(opts: {
  name: string;
  description: string;
  url: string;
  image?: string;
  lowPrice: number;
  highPrice?: number;
  currency?: string;
}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: opts.name,
    description: opts.description,
    url: `${BASE_URL}${opts.url}`,
    image: opts.image ?? `${BASE_URL}/og-cover.jpg`,
    brand: {
      '@type': 'Brand',
      name: 'Big Bird Travel and Tourism',
    },
    // Single Offer (each package has one "from" price, not a real range), so
    // the AggregateOffer-only fields highPrice/offerCount no longer apply and
    // Google stops flagging them as missing. priceValidUntil is set ~1 year out.
    offers: {
      '@type': 'Offer',
      price: opts.lowPrice,
      priceCurrency: opts.currency ?? 'AED',
      availability: 'https://schema.org/InStock',
      url: `${BASE_URL}${opts.url}`,
      priceValidUntil: new Date(new Date().setFullYear(new Date().getFullYear() + 1))
        .toISOString()
        .slice(0, 10),
      seller: {
        '@type': 'TravelAgency',
        name: 'Big Bird Travel and Tourism',
        url: BASE_URL,
      },
    },
  };
}

// ── ItemList schema for TouristTrip packages ─────────────────────────────────
export function itemListSchema(packages: {
  name: string;
  url: string;
  description: string;
  image?: string;
  priceFrom: number;
  currency?: string;
}[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'Holiday Packages from UAE',
    description: 'Curated holiday packages from the UAE for families, couples, and groups',
    numberOfItems: packages.length,
    itemListElement: packages.map((pkg, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      item: {
        '@type': 'TouristTrip',
        name: pkg.name,
        description: pkg.description,
        url: `${BASE_URL}${pkg.url}`,
        image: pkg.image ?? `${BASE_URL}/og-cover.jpg`,
        offers: {
          '@type': 'Offer',
          price: pkg.priceFrom,
          priceCurrency: pkg.currency ?? 'AED',
          availability: 'https://schema.org/InStock',
        },
        provider: {
          '@type': 'TravelAgency',
          name: 'Big Bird Travel and Tourism',
          url: BASE_URL,
        },
      },
    })),
  };
}

// ── Article schema for blog posts ─────────────────────────────────────────────
export function articleSchema(opts: {
  headline: string;
  description: string;
  url: string;
  image?: string;
  datePublished: string;
  dateModified?: string;
  authorName?: string;
}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: opts.headline,
    description: opts.description,
    url: `${BASE_URL}${opts.url}`,
    image: opts.image ?? `${BASE_URL}/og-cover.jpg`,
    datePublished: opts.datePublished,
    dateModified: opts.dateModified ?? opts.datePublished,
    author: {
      '@type': 'Organization',
      name: opts.authorName ?? 'Big Bird Travel and Tourism',
      url: BASE_URL,
    },
    publisher: {
      '@type': 'Organization',
      name: 'Big Bird Travel and Tourism',
      logo: { '@type': 'ImageObject', url: `${BASE_URL}/logo.png` },
    },
    mainEntityOfPage: { '@type': 'WebPage', '@id': `${BASE_URL}${opts.url}` },
  };
}

// ── Organization schema (for homepage) ───────────────────────────────────────
export const ORGANIZATION_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'Big Bird Travel and Tourism',
  url: BASE_URL,
  logo: `${BASE_URL}/logo.png`,
  contactPoint: [
    {
      '@type': 'ContactPoint',
      telephone: '+971561789642',
      contactType: 'customer service',
      availableLanguage: ['English', 'Arabic'],
      areaServed: 'AE',
    },
    {
      '@type': 'ContactPoint',
      telephone: '+971561789642',
      contactType: 'sales',
      availableLanguage: ['English', 'Arabic'],
      areaServed: 'AE',
    },
  ],
  sameAs: [
    'https://www.instagram.com/bigbirdtraveluae/',
    'https://www.facebook.com/profile.php?id=61558812744882',
  ],
};

// ── WebSite schema with SearchAction (enables Google Sitelinks search) ────────
export const WEBSITE_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: 'Big Bird Travel and Tourism UAE',
  url: BASE_URL,
  potentialAction: {
    '@type': 'SearchAction',
    target: { '@type': 'EntryPoint', urlTemplate: `${BASE_URL}/tour-packages?q={search_term_string}` },
    'query-input': 'required name=search_term_string',
  },
};
